import Footer from "@src/components/common/footer";
import Layout from "@src/components/common/Layout";
import Loading from "@src/components/common/Loading";
import ReportNotAvailable from "@src/components/powerBiEmbed/ReportNotAvailable";
import { useGetUserReportsQuery, useLazyGetReportQuery } from "@src/services/reports";
import { IReports } from "@src/shared/interfaces/reports.interface";
import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect, useMemo } from "react";

import { NextPageWithLayout } from "../_app";

const PowerBiEmbed = dynamic(() => import("@src/components/powerBiEmbed"), { ssr: false });

interface ReportProps {
  reports: IReports[];
}

const Report: NextPageWithLayout<ReportProps> = () => {
  const router = useRouter();
  const { slugs } = router.query;
  const [workspaceId, externalId] = Array.isArray(slugs) ? slugs : [];

  const [triggerReport, { isLoading, data, isFetching, isError }] = useLazyGetReportQuery();
  const { data: userReportsData } = useGetUserReportsQuery({});
  const { token, reportFilters, reportPages } = data || {};

  useEffect(() => {
    if (!workspaceId && !externalId) return;
    triggerReport({ workspaceId, externalId });
  }, [workspaceId, externalId, triggerReport]);

  const { hasAccess, reportIsActive, reportTitle } = useMemo(() => {
    if (isError) {
      return {
        hasAccess: false,
        reportIsActive: false,
        reportTitle: "Relatório Indisponível",
      };
    }

    const reports = userReportsData?.reports;
    if (!Array.isArray(reports)) {
      return {
        hasAccess: undefined,
        reportIsActive: false,
        reportTitle: null,
      };
    }

    const report = reports.find((userReport: IReports) => userReport.externalId === externalId);
    const access = Boolean(report);
    const isActive = Boolean(report?.isActive);
    let title: string | null = null;
    if (report && isActive && report.title) {
      title = `Relatório | ${report.title}`;
    } else if (!isActive) {
      title = "Relatório Indisponível";
    }

    return {
      hasAccess: access,
      reportIsActive: isActive,
      reportTitle: title,
    };
  }, [userReportsData, externalId, isError]);

  const pageTitle = reportTitle || "Carregando Relatórios";

  const isAccessStatusPending = hasAccess === undefined && !isError;
  const isLoadingReport = isLoading || isFetching || isAccessStatusPending;

  const showReport = hasAccess && reportIsActive && !isError;

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      {isLoadingReport ? (
        <div className="w-full h-screen relative flex justify-center items-center">
          <Loading />
        </div>
      ) : (
        <>
          {showReport ? (
            <PowerBiEmbed
              externalId={externalId}
              token={token}
              filters={reportFilters}
              activePages={reportPages}
            />
          ) : (
            <>
              <ReportNotAvailable />
              <div className="bottom-0 absolute">
                <Footer />
              </div>
            </>
          )}
        </>
      )}
    </>
  );
};

Report.getLayout = function getLayout(page) {
  return <Layout>{page}</Layout>;
};

export default Report;
